import { BehaviorSubject } from 'rxjs';
import store from 'store';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isSet from 'lodash/isSet';
import { ImmerCallback } from './type';
import isFunction from 'lodash/isFunction';
import { produce } from 'immer';
import { useBehaviorSubject, BehaviorSubjectSelector } from './util/useBehaviorSubject';

type Input<State> = {
    initialState: State;
    cacheKey?: string;
};

export function createStore$<State>(input: Input<State>) {
    const { initialState, cacheKey = '' } = input;

    const subject = new BehaviorSubject<State>(cacheKey ? getCachedState() : getInitialState());

    if (cacheKey) {
        subject.subscribe((next) => {
            store.set(cacheKey, next);
        });
    }

    function getCachedState() {
        const value = store.get(cacheKey);
        return value === undefined ? getInitialState() : value;
    }

    function getInitialState(): any {
        const value = initialState;

        if (isArray(value)) {
            return [...value];
        } else if (isSet(value)) {
            return new Set(value);
        } else if (isObject(value)) {
            return { ...value };
        } else {
            return value;
        }
    }

    return {
        $: subject,
        subscribe: (callback: (value: State) => void) => subject.subscribe(callback),
        state: subject,
        useState<O = State>(selector?: BehaviorSubjectSelector<State, O>) {
            return useBehaviorSubject(subject, selector);
        },
        useValue<O = State>(selector?: BehaviorSubjectSelector<State, O>) {
            return useBehaviorSubject(subject, selector)[0];
        },
        reset() {
            subject.next(getInitialState());
        },
        getValue() {
            return subject.getValue();
        },
        setValue(value: State | ImmerCallback<State>) {
            if (isFunction(value)) {
                subject.next(produce(subject.getValue(), value));
            } else {
                subject.next(value);
            }
        },
    };
}
